import React, { createContext, useContext, useState, useEffect } from 'react';
import { storage } from '../services/storage';

export interface AIConfig {
    textGeneration: {
        enabled: boolean;
        provider: string;
        model: string;
        keys: Record<string, string>; // Changed from apiKey to keys map
        temperature: number;
        guardrails?: {
            forbiddenWords: string[];
            tone: string;
        };
        templates?: {
            blog: string;
            page: string;
        };
    };
    videoGeneration: {
        enabled: boolean;
        provider: string;
        model: string;
        keys: Record<string, string>;
        fps: number;
    };
    chatbot: {
        enabled: boolean;
        provider: string;
        model: string;
        keys: Record<string, string>;
        ragEnabled: boolean;
        guardrails: {
            enabled: boolean;
            blockedTopics: string[];
        };
        localModel: {
            enabled: boolean;
            url: string;
        };
    };
    imageGeneration: {
        enabled: boolean;
        provider: string;
        model: string;
        keys: Record<string, string>;
    };
    audioGeneration: {
        enabled: boolean;
        provider: string;
        voice: string;
        keys: Record<string, string>;
    };
}

const DEFAULT_CONFIG: AIConfig = {
    textGeneration: {
        enabled: true,
        provider: 'openai',
        model: 'gpt-4-turbo',
        keys: {
            openai: import.meta.env.VITE_OPENAI_API_KEY || '',
            anthropic: import.meta.env.VITE_ANTHROPIC_API_KEY || '',
            google: import.meta.env.VITE_GOOGLE_API_KEY || ''
        },
        temperature: 0.7,
        guardrails: {
            forbiddenWords: [],
            tone: 'Professional'
        },
        templates: {
            blog: '',
            page: ''
        }
    },
    videoGeneration: {
        enabled: false,
        provider: 'runway',
        model: 'gen-2',
        keys: {
            runway: import.meta.env.VITE_RUNWAY_API_KEY || '',
            pika: import.meta.env.VITE_PIKA_API_KEY || '',
            stability: import.meta.env.VITE_STABILITY_API_KEY || ''
        },
        fps: 24
    },
    chatbot: {
        enabled: true,
        provider: 'anthropic',
        model: 'claude-3-opus',
        keys: {
            openai: import.meta.env.VITE_OPENAI_API_KEY || '',
            anthropic: import.meta.env.VITE_ANTHROPIC_API_KEY || ''
        },
        ragEnabled: false,
        guardrails: {
            enabled: false,
            blockedTopics: []
        },
        localModel: {
            enabled: false,
            url: 'http://localhost:11434/v1/chat/completions'
        }
    },
    imageGeneration: {
        enabled: true,
        provider: 'unsplash',
        model: 'standard',
        keys: {
            dalle: import.meta.env.VITE_OPENAI_API_KEY || '',
            stability: import.meta.env.VITE_STABILITY_API_KEY || ''
        }
    },
    audioGeneration: {
        enabled: true,
        provider: 'openai',
        voice: 'alloy',
        keys: {
            openai: import.meta.env.VITE_OPENAI_API_KEY || ''
        }
    }
};

interface AIContextType {
    config: AIConfig;
    updateConfig: (newConfig: AIConfig) => Promise<void>;
    loading: boolean;
    isChatOpen: boolean;
    toggleChat: () => void;
}

const AIContext = createContext<AIContextType | undefined>(undefined);

export const AIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [config, setConfig] = useState<AIConfig>(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);

    const [isChatOpen, setIsChatOpen] = useState(false);

    useEffect(() => {
        const loadConfig = async () => {
            await storage.delay(500); // Simulate network load
            const savedConfig = storage.get<Partial<AIConfig>>('ai_config', {});

            // Deep merge to ensure all new fields are present
            const mergedConfig: AIConfig = {
                ...DEFAULT_CONFIG,
                ...savedConfig,
                textGeneration: { ...DEFAULT_CONFIG.textGeneration, ...savedConfig.textGeneration },
                videoGeneration: { ...DEFAULT_CONFIG.videoGeneration, ...savedConfig.videoGeneration },
                chatbot: {
                    ...DEFAULT_CONFIG.chatbot,
                    ...savedConfig.chatbot,
                    guardrails: { ...DEFAULT_CONFIG.chatbot.guardrails, ...(savedConfig.chatbot?.guardrails || {}) },
                    localModel: { ...DEFAULT_CONFIG.chatbot.localModel, ...(savedConfig.chatbot?.localModel || {}) }
                },
                imageGeneration: { ...DEFAULT_CONFIG.imageGeneration, ...savedConfig.imageGeneration },
                audioGeneration: { ...DEFAULT_CONFIG.audioGeneration, ...(savedConfig as any).audioGeneration },
            };

            setConfig(mergedConfig);
            setLoading(false);
        };
        loadConfig();
    }, []);

    const updateConfig = async (newConfig: AIConfig) => {
        setLoading(true);
        await storage.delay(1000); // Simulate network save
        storage.set('ai_config', newConfig);
        setConfig(newConfig);
        setLoading(false);
    };

    const toggleChat = () => setIsChatOpen(prev => !prev);

    return (
        <AIContext.Provider value={{ config, updateConfig, loading, isChatOpen, toggleChat }}>
            {children}
        </AIContext.Provider>
    );
};

export const useAI = () => {
    const context = useContext(AIContext);
    if (context === undefined) {
        throw new Error('useAI must be used within an AIProvider');
    }
    return context;
};
